<template>
  <v-app>
    <!-- Navigation Drawer Lateral -->
    <v-navigation-drawer
      v-model="patientProfileLeftMenu"
      :mini-variant.sync="mini"
      :width="300"
      app
      permanent
      :class="{ 'drawer-behind-backdrop': appointmentDialog }"
    >
      <!-- Cabeçalho do perfil -->
      <div v-if="!mini" class="drawer-section-header">
        <v-icon small color="primary" class="mr-2">mdi-account-circle</v-icon>
        <span class="drawer-section-title">Perfil</span>
      </div>

      <!-- Card do paciente -->
      <div
        class="patient-card"
        :class="{ 'patient-card-mini': mini }"
        data-tour="pd-patient"
      >
        <v-list-item class="px-3">
          <v-list-item-avatar
            class="patient-avatar d-flex justify-center align-center"
            :size="mini ? 40 : 48"
          >
            <img :src="imageProfile" alt="Paciente" />
          </v-list-item-avatar>

          <v-list-item-content v-if="!mini" class="ml-2">
            <v-list-item-title class="patient-name">
              {{ patient.name }}
            </v-list-item-title>
            <v-list-item-subtitle class="patient-age">
              <v-icon x-small color="grey" class="mr-1"
                >mdi-cake-variant</v-icon
              >
              {{ calculateAge(patient.birth_date) }} anos
            </v-list-item-subtitle>
          </v-list-item-content>

        </v-list-item>
      </div>

      <template v-if="!mini">
        <v-divider></v-divider>

        <!-- Informações rápidas -->
        <v-list dense class="py-2">
          <v-list-item class="info-item">
            <v-list-item-icon class="info-icon">
              <v-icon color="primary">mdi-gender-male-female</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="info-value">
                {{ patient.gender || "—" }}
              </v-list-item-title>
              <v-list-item-subtitle class="info-label"
                >Sexo</v-list-item-subtitle
              >
            </v-list-item-content>
          </v-list-item>

          <v-list-item class="info-item">
            <v-list-item-icon class="info-icon">
              <v-icon color="primary">mdi-calendar</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="info-value">
                {{ formatBirthDate(patient.birth_date) || "—" }}
              </v-list-item-title>
              <v-list-item-subtitle class="info-label">
                Data de Nascimento
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!--
            salvia-773: o tipo sanguíneo saiu daqui. Ele é pouco usado no dia a
            dia e continua no diálogo de Detalhes; quem o médico precisa ver na
            visão rápida é quando o paciente volta.
          -->
          <v-list-item class="info-item next-appt">
            <v-list-item-icon class="info-icon">
              <v-icon color="primary">mdi-calendar-clock</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title
                class="info-value next-appt__value"
                :class="{ 'next-appt__value--vazio': !nextAppointment }"
              >
                {{ nextAppointmentText }}
              </v-list-item-title>
              <v-list-item-subtitle class="info-label next-appt__label">
                Próximo Agendamento
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>


        <!-- Botões de ação -->
        <div class="px-3 pb-3 d-flex action-buttons">
          <v-btn
            outlined
            color="primary"
            small
            class="flex-grow-1 mr-2"
            @click="checkProfileDetails"
          >
            <v-icon x-small left>mdi-card-account-details-outline</v-icon>
            Detalhes
          </v-btn>
          <v-btn
            outlined
            color="primary"
            small
            class="flex-grow-1"
            @click="handleScheduleAppointment"
          >
            <v-icon x-small left>mdi-calendar-plus</v-icon>
            Agendar
          </v-btn>
        </div>
      </template>

      <v-divider v-if="!mini"></v-divider>

      <!-- Seções -->
      <div v-if="!mini" class="drawer-section-header mt-2">
        <v-icon small color="primary" class="mr-2">mdi-view-dashboard</v-icon>
        <span class="drawer-section-title">Seções</span>
      </div>

      <v-list dense class="section-list" data-tour="pd-sections">
        <v-list-item
          v-for="item in items"
          :key="item.title"
          link
          class="section-item"
          :data-tour="item.tour"
          @click="handleSectionClick(item.title)"
        >
          <v-list-item-icon class="info-icon">
            <v-icon color="primary">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content v-if="!mini">
            <v-list-item-title class="section-title">
              {{ item.title }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action v-if="!mini">
            <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- Backdrop escurecido quando dialog aberto -->
    <div v-if="appointmentDialog" class="dialog-backdrop"></div>

    <!-- Formulário fixo à esquerda -->
    <AppointmentDialog
      v-if="appointmentDialog"
      ref="appointmentDialogRef"
      :appointment-id="selectedAppointmentId"
      :source-event-id="sourceEventId"
      @closeRightMenu="appointmentDialog = false"
      @appointment-saved="handleAppointmentSaved"
      @open-schedule-dialog="handleScheduleAppointment"
    />

    <v-container class="d-flex mb-0 px-6" cols="4">
      <!-- Campo de Pesquisa e Botões -->
      <v-row class="d-flex justify-center align-center">
        <v-col cols="10">
          <v-row class="align-center" no-gutters>
            <!-- Campo de Pesquisa -->
            <v-col class="d-flex align-center" data-tour="pd-search">
              <v-text-field
                v-model="searchQuery"
                label="Pesquisar"
                prepend-icon="mdi-magnify"
                clearable
                outlined
                color="blue"
                class="mr-2"
                hide-details
                style="height: 40px"
              />
            </v-col>

            <!-- Botão de reordenar / concluir -->
            <v-col cols="auto" class="d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    color="primary"
                    height="40"
                    class="mr-2"
                    :loading="savingOrder"
                    data-tour="pd-reorder"
                    @click="toggleEditMode"
                  >
                    <v-icon left small>
                      {{ editMode ? "mdi-check" : "mdi-drag-variant" }}
                    </v-icon>
                    {{ editMode ? "Concluir" : "Reordenar" }}
                  </v-btn>
                </template>
                <span>
                  {{
                    editMode
                      ? "Concluir reordenação dos cards"
                      : "Arrastar para reordenar os cards do dashboard"
                  }}
                </span>
              </v-tooltip>

              <!-- Restaurar padrão — só aparece no modo edição -->
              <v-tooltip v-if="editMode" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    color="grey darken-1"
                    class="mr-2"
                    @click="restoreDefaultOrder"
                  >
                    <v-icon>mdi-restore</v-icon>
                  </v-btn>
                </template>
                <span>Restaurar ordem padrão</span>
              </v-tooltip>
            </v-col>

            <!-- Botão Azul: Novo Atendimento -->
            <v-col cols="auto" class="d-flex align-center">
              <v-btn
                color="primary"
                height="40"
                class="text-white font-weight-bold"
                data-tour="pd-novo-atend"
                @click="openAppointmentDialog()"
              >
                Novo Atendimento
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <v-container
      class="mt-0 cards-container"
      :class="{ 'dialog-open': appointmentDialog }"
    >
      <!-- Quando dialog aberto: cards em expansion panels colapsáveis.
           SALVIA-933: sem `multiple` — acordeão, um aberto por vez; clicar no
           aberto o recolhe. -->
      <template v-if="appointmentDialog">
        <v-expansion-panels
          v-model="expandedPanels"
          flat
          class="collapsed-cards"
        >
          <!-- Cards dinâmicos -->
          <v-expansion-panel
            v-for="(card, index) in filteredCards"
            :key="index + '-' + componentKey"
          >
            <v-expansion-panel-header class="collapsed-card-header">
              <span class="collapsed-card-title">{{ card.title || "Card" }}</span>
              <!-- SALVIA-938: itens do card, visível com a caixa fechada. -->
              <span
                v-if="cardCounts[card.id] != null"
                class="collapsed-card-count"
                :class="{ 'collapsed-card-count--empty': !cardCounts[card.id] }"
                :title="`${cardCounts[card.id]} ${cardCounts[card.id] === 1 ? 'item' : 'itens'}`"
              >{{ cardCounts[card.id] }}</span>
            </v-expansion-panel-header>
            <!-- eager: monta o card mesmo com a caixa fechada, para o contador
                 existir antes de abrir. Os dados vêm do cache da página
                 (patientDashboardStore) — sem requisição extra. -->
            <v-expansion-panel-content eager>
              <component
                :is="card.component"
                :patient-id="patientId"
                :info-title="card.title"
                :show-quick-add="true"
                @quick-add-saved="forwardQuickInsert"
                @atualizarDados="handleDataUpdated"
                @card-loaded="onCardLoaded(card.id)"
                @item-count="setCardCount(card.id, $event)"
                :key="'card-' + index + '-' + componentKey"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>

      <!-- Quando dialog fechado: layout normal -->
      <template v-else>
        <div class="cards-loading-wrapper">
          <div :class="{ 'cards-blurred': cardsLoading }">
            <!-- Banner informativo do modo edição -->
            <v-alert
              v-if="editMode"
              dense
              text
              color="primary"
              icon="mdi-drag-variant"
              class="mb-3 edit-mode-banner"
            >
              Arraste os cards para reorganizar. A nova ordem é salva
              automaticamente e fica disponível em todos os pacientes.
            </v-alert>

            <draggable
              v-model="cards"
              tag="div"
              class="row cards-draggable"
              :class="{ 'is-edit-mode': editMode }"
              :disabled="!editMode"
              handle=".card-drag-area"
              ghost-class="card-ghost"
              chosen-class="card-chosen"
              drag-class="card-dragging"
              animation="180"
              @end="onCardDragEnd"
            >
              <!-- Em modo edição: lista completa (sem filtro de busca) -->
              <!-- Modo normal: respeita o filtro do searchQuery -->
              <v-col
                v-for="(card, ci) in editMode ? cards : filteredCards"
                :key="card.id + '-' + componentKey"
                :data-tour="ci === 0 ? 'pd-cards' : null"
                :cols="getCardCols()"
                :sm="getCardSm()"
                :md="getCardMd(card)"
                :lg="getCardLg(card)"
                :xl="getCardXl(card)"
                class="d-flex flex-column card-drag-area"
              >
                <component
                  :is="card.component"
                  :patient-id="patientId"
                  :info-title="card.title"
                  :show-quick-add="true"
                  @quick-add-saved="forwardQuickInsert"
                  @atualizarDados="handleDataUpdated"
                  @card-loaded="onCardLoaded(card.id)"
                  :key="'card-' + card.id + '-' + componentKey"
                />
              </v-col>
            </draggable>
          </div>

          <!-- Overlay de carregamento: cobre os cards enquanto nem todos
               terminaram de carregar, evitando o "pop-in" um a um. -->
          <div v-if="cardsLoading" class="cards-loading-overlay">
            <v-progress-circular
              indeterminate
              size="56"
              width="5"
              color="primary"
            />
            <span class="cards-loading-text">
              Carregando dados do paciente…
            </span>
          </div>
        </div>
      </template>

      <!-- Overlay para escurecer o fundo -->
      <div
        v-if="showComponent"
        class="custom-overlay"
        @click="showComponent = false"
      ></div>

      <!-- Drawer personalizado -->
      <div class="custom-drawer" :class="{ 'drawer-open': showComponent }">
        <button
          @click="showComponent = false"
          class="v-btn v-btn--icon v-btn--round v-btn--dense close-btn"
          style="position: absolute; top: 15px; right: 20px"
        >
          <v-icon>mdi-close</v-icon>
        </button>

        <component
          :is="currentComponent"
          :key="'drawer-' + currentComponent + '-' + drawerRefreshSeq"
          @close="showComponent = false"
          @open-appointment-dialog="handleOpenAppointmentDialog"
        ></component>
      </div>
    </v-container>

    <!-- Componente de agendamento -->
    <CalendarDialogAddNewEvent
      v-model="openDialogAddNewEvent"
      :preSelectedPatient="AddNewEventPreSelectedPatient"
      :preSelectedDate="selectedTimeForNewEvent ? selectedTimeForNewEvent.date : null"
      :preSelectedTime="selectedTimeForNewEvent ? selectedTimeForNewEvent.time : null"
    />

    <!-- COMPONENTE DIALOG -->
    <PersonDetailsDialog
      v-model="detailsDialogVisible"
      :personData="patient"
      cardType="patient"
    />
  </v-app>
</template>

<script>
import http from "@/services/http";

// ─── Cards do dashboard: cada um é um componente independente que recebe apenas
// `patient-id` (busca os próprios dados). Os wrappers MainGoal/GoalsDeadlines/
// Complementary/Chronic/Psychological encapsulam o ConditionsCard com sua config
// (category) — assim o dashboard não depende de um config central.
import LifeHistoryCard from "@/features/patients/profile-dashboard/cards/LifeHistoryCard.vue";
import BodyCompositionCard from "@/features/patients/profile-dashboard/cards/BodyCompositionCard.vue";
import MainGoalCard from "@/features/patients/profile-dashboard/cards/MainGoalCard.vue";
import GoalsDeadlinesCard from "@/features/patients/profile-dashboard/cards/GoalsDeadlinesCard.vue";
import CidsCard from "@/features/patients/profile-dashboard/cards/CidsCard.vue";
import ChronicDiseasesCard from "@/features/patients/profile-dashboard/cards/ChronicDiseasesCard.vue";
import MedicationsCard from "@/features/patients/profile-dashboard/cards/MedicationsCard.vue";
import SupplementsCard from "@/features/patients/profile-dashboard/cards/SupplementsCard.vue";
import DietaryRestrictionsCard from "@/features/patients/profile-dashboard/cards/DietaryRestrictionsCard.vue";
import SymptomsCard from "@/features/patients/profile-dashboard/cards/SymptomsCard.vue";
import AllergiesCard from "@/features/patients/profile-dashboard/cards/AllergiesCard.vue";
import PsychologicalDisordersCard from "@/features/patients/profile-dashboard/cards/PsychologicalDisordersCard.vue";
import SleepCard from "@/features/patients/profile-dashboard/cards/SleepCard.vue";
import GynecologyCard from "@/features/patients/profile-dashboard/cards/GynecologyCard.vue";
import ComplementaryTreatmentsCard from "@/features/patients/profile-dashboard/cards/ComplementaryTreatmentsCard.vue";
import ExercisePracticeCard from "@/features/patients/profile-dashboard/cards/ExercisePracticeCard.vue";
import AlcoholCard from "@/features/patients/profile-dashboard/cards/AlcoholCard.vue";
import HydrationCard from "@/features/patients/profile-dashboard/cards/HydrationCard.vue";
import DietaryRecallCard from "@/features/patients/profile-dashboard/cards/DietaryRecallCard.vue";
import PatientInsightsCard from "@/features/patients/profile-dashboard/cards/PatientInsightsCard.vue";
import Goals from "@/features/patients/components/Goals.vue";
import Exams from "@/features/patients/exams/Exams.vue";
import ChatDoctor from "@/features/chat/ChatDoctor.vue";

import Appointments from "@/features/patients/appointments/Appointments.vue";
import AppointmentDialog from "@/features/patients/appointments/AppointmentDialog.vue";

import Prescriptions from "@/features/patients/prescriptions/Prescriptions.vue";
import AnamnesisContainer from "@/features/patients/components/AnamnesisContainer.vue";
import CorrelatedItems from "@/features/patients/profile-dashboard/shared/CorrelatedItems.vue";
import CalendarDialogAddNewEvent from "@/features/calendar/components/CalendarDialogAddNewEvent.vue";
import PersonDetailsDialog from "@/features/patients/components/PersonDetailsDialog.vue";

import { calculateAge } from "@/utils/calculateAge.js";
import { formatBirthDate } from "@/utils/formatBirthDate.js";
import { getProfileImage } from "@/utils/getProfileImage";

import draggable from "vuedraggable";
import { dashboardCards } from "@/features/patients/profile-dashboard/config/dashboardCards.js";
import {
  getPreference,
  savePreference,
  PREF_KEYS,
} from "@/services/userPreferencesService.js";
import tourMixin from "@/features/tour/tourMixin";
import { formatDateTime } from "@/utils/datetime";

export default {
  mixins: [tourMixin],

  components: {
    AppointmentDialog,
    Goals,
    Exams,
    Appointments,
    Prescriptions,
    AnamnesisContainer,
    CorrelatedItems,
    CalendarDialogAddNewEvent,
    PersonDetailsDialog,
    LifeHistoryCard,
    BodyCompositionCard,
    MainGoalCard,
    GoalsDeadlinesCard,
    CidsCard,
    ChronicDiseasesCard,
    MedicationsCard,
    SupplementsCard,
    DietaryRestrictionsCard,
    SymptomsCard,
    AllergiesCard,
    PsychologicalDisordersCard,
    SleepCard,
    GynecologyCard,
    ComplementaryTreatmentsCard,
    ExercisePracticeCard,
    AlcoholCard,
    HydrationCard,
    DietaryRecallCard,
    PatientInsightsCard,
    ChatDoctor,
    draggable,
  },

  data() {
    return {
      detailsDialogVisible: false,
      searchQuery: "",
      cards: [],
      patientProfileLeftMenu: false,
      mini: false,
      appointmentDialog: false,
      selectedAppointmentId: null,
      // Histórico do kanban: o evento da agenda que originou o atendimento
      // aberto pela rota (`?atendimento=novo&event=ID`).
      sourceEventId: null,
      openDialogAddNewEvent: false,
      AddNewEventPreSelectedPatient: null,
      selectedTimeForNewEvent: null,
      patient: {},
      loadingPatient: false,
      // salvia-773: data/hora do próximo agendamento, exibida no drawer.
      // `null` = ainda carregando; `""` = consultado e não há nenhum.
      nextAppointment: null,

      currentComponent: null,
      showComponent: false,
      // imagem do paciente
      imageProfile: null,
      _patientBlobUrl: null,
      componentKey: 0,
      expandedPanels: null,
      /** SALVIA-938: quantidade de itens por card (id → número), para as caixas laterais. */
      cardCounts: {},
      items: [
        { title: "Atendimentos", icon: "mdi-clipboard-pulse-outline", tour: "sec-atendimentos" },
        { title: "Anamneses", icon: "mdi-file-document-edit-outline", tour: "sec-anamneses" },
        { title: "Metas", icon: "mdi-target", tour: "sec-metas" },
        { title: "Exames", icon: "mdi-test-tube", tour: "sec-exames" },
        { title: "Prescrições", icon: "mdi-prescription", tour: "sec-prescricoes" },
        { title: "Itens Correlacionados", icon: "mdi-link-variant", tour: "sec-correlacionados" },
        { title: "Chat", icon: "mdi-chat-processing-outline", tour: "sec-chat" },
      ],
      showInitiateAnamnesisDialog: false,
      sending: false,
      // ─── Reordenação de cards ─────────────────────────────
      editMode: false,
      savingOrder: false,
      _saveOrderTimeout: null,
      // ─── Carregamento inicial dos cards ───────────────────
      cardsLoading: true,
      loadedCardIds: [],
      _loadingSafetyTimeout: null,
      // Incrementa a cada abertura de aba do drawer; usado como :key para
      // forçar remontagem (e recarga) mesmo ao reabrir a MESMA aba.
      drawerRefreshSeq: 0,
    };
  },

  watch: {
    /**
     * salvia-779: o `created` só lê o `?atendimento=novo` na CHEGADA. Com o
     * prontuário deste mesmo paciente já aberto, o clique no aviso "consulta
     * começando" muda só a query — o <router-view> tem a mesma chave e a tela
     * não é recriada. Sem isto o clique não fazia nada.
     *
     * Com um atendimento já aberto não abre outro por cima: o médico está
     * nele, e o aviso já cumpriu o papel de trazê-lo aqui.
     */
    "$route.query.atendimento"(value) {
      if (value !== "novo") return;
      if (this.appointmentDialog) {
        const query = { ...this.$route.query };
        delete query.atendimento;
        delete query.event;
        this.$router.replace({ query }).catch(() => {});
        return;
      }
      this.openNewAppointmentFromRoute();
    },

    // Mesmo motivo do watcher acima: com o prontuário já aberto, o "Abrir
    // consulta" do histórico do kanban muda só a query.
    "$route.query.appointment"(value) {
      if (value) this.openAppointmentFromRoute();
    },
  },

  created() {
    this.getPatientById();
    this.fetchNextAppointment();
    this.createCard();
    this.loadCustomCardOrder();
    this.startCardsLoading();
    this.openNewAppointmentFromRoute();
    this.openAppointmentFromRoute();
    this.openChatFromRoute();
  },

  beforeDestroy() {
    this.revokePatientBlobUrl();
    if (this._saveOrderTimeout) clearTimeout(this._saveOrderTimeout);
    if (this._loadingSafetyTimeout) clearTimeout(this._loadingSafetyTimeout);
  },

  computed: {
    tourId() {
      return "perfil-paciente";
    },
    nextAppointmentText() {
      if (this.nextAppointment === null) return "—";
      if (!this.nextAppointment) return "Sem agendamento futuro";

      return formatDateTime(this.nextAppointment);
    },
    patientId() {
      return this.$route.params.id;
    },

    filteredCards() {
      const normalizeText = (text) =>
        (text || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
      const normalizedSearchQuery = normalizeText(this.searchQuery);
      return this.cards.filter((card) =>
        normalizeText(card.title).includes(normalizedSearchQuery),
      );
    },
  },

  methods: {
    calculateAge: (v) => calculateAge(v, { fallback: "Data inválida" }),
    formatBirthDate,

    // ================= TOUR GUIADO =================
    tourSteps() {
      return [
        {
          title: "Perfil do paciente 🩺",
          description:
            "Esta é a tela central do paciente: à esquerda os dados e as seções; ao centro, os cards clínicos. Vou mostrar o essencial — reabra pelo “?” quando quiser.",
        },
        {
          anchor: "pd-patient",
          title: "O paciente em foco",
          description:
            "No topo do menu ficam a foto, o nome e os dados rápidos (sexo, idade, tipo sanguíneo…) do paciente que você está visualizando.",
        },
        {
          anchor: "pd-sections",
          title: "Menu de seções",
          description:
            "Este menu à esquerda leva às seções do prontuário. Vou explicar cada uma delas a seguir — todas abrem no centro da tela.",
        },
        {
          anchor: "sec-atendimentos",
          title: "Atendimentos",
          description:
            "O histórico de atendimentos/consultas do paciente — onde ficam registrados os acompanhamentos ao longo do tempo.",
        },
        {
          anchor: "sec-anamneses",
          title: "Anamneses",
          description:
            "As anamneses respondidas deste paciente — baseadas nos modelos que você configura em “Minhas Anamneses”.",
        },
        {
          anchor: "sec-metas",
          title: "Metas",
          description:
            "As metas e objetivos definidos para o paciente, com seus prazos e acompanhamento.",
        },
        {
          anchor: "sec-exames",
          title: "Exames",
          description:
            "Os exames solicitados ao paciente e o acompanhamento dos resultados.",
        },
        {
          anchor: "sec-prescricoes",
          title: "Prescrições",
          description:
            "As prescrições emitidas (medicações, exames, suplementos), com os detalhes e os CIDs vinculados. É de onde você imprime/consulta a receita.",
        },
        {
          anchor: "sec-correlacionados",
          title: "Itens Correlacionados",
          description:
            "Reúne as correlações entre os itens do paciente — por exemplo, um medicamento que trata um CID — numa visão única.",
        },
        {
          anchor: "sec-chat",
          title: "Chat",
          description:
            "Espaço de mensagens/conversa relacionado ao acompanhamento do paciente.",
        },
        {
          anchor: "pd-novo-atend",
          title: "Novo Atendimento",
          description:
            "Abre o registro de um novo acompanhamento/consulta: é onde você documenta a evolução, as condutas e o que foi tratado naquele encontro com o paciente.",
        },
        {
          anchor: "pd-search",
          title: "Busque um card",
          description:
            "Filtre os cards clínicos pelo nome (ex.: “alergias”, “sono”) para achar rápido a informação que procura.",
        },
        {
          anchor: "pd-reorder",
          title: "Reordenar os cards",
          description:
            "Ative o modo de reordenação e arraste os cards para a ordem que preferir. A nova ordem é salva e vale para todos os pacientes.",
        },
        {
          anchor: "pd-cards",
          title: "Os cards clínicos",
          description:
            "Cada card resume uma área do paciente (alergias, medicações, metas, sono, CIDs…). Ao salvar em um, os que compartilham informação se atualizam juntos.",
        },
        {
          anchor: "pd-card-menu",
          title: "Menu do card (3 pontinhos)",
          description:
            "O botão de três pontinhos no topo de cada card abre a janela para adicionar ou editar os dados daquele card — por exemplo, incluir uma alergia ou ajustar uma medicação.",
        },
      ];
    },

    // ================= PERFIL =================
    checkProfileDetails() {
      this.detailsDialogVisible = true;
    },

    /**
     * Próximo agendamento do paciente (salvia-773).
     *
     * Reusa /api/events/upcoming, que já devolve só o que está no futuro, já
     * descarta cancelado e já vem em ordem crescente — o primeiro da lista é o
     * próximo. Não precisou de endpoint novo.
     */
    async fetchNextAppointment() {
      const patientId = this.$route.params.id;
      if (!patientId) return;

      try {
        const { data } = await http.get("/api/events/upcoming", {
          params: { patient_id: patientId },
        });

        const first = (data?.data || [])[0];
        this.nextAppointment = first?.start || "";
      } catch (error) {
        console.error("Erro ao buscar o próximo agendamento:", error);
        // Sem agendamento à vista é diferente de falha de rede, mas para quem
        // olha o drawer o efeito é o mesmo: nada a mostrar.
        this.nextAppointment = "";
      }
    },

    async getPatientById() {
      const patientId = this.$route.params.id;

      this.loadingPatient = true;
      this.revokePatientBlobUrl();

      try {
        const { data } = await http.get(
          `/api/relation/get-patient-by-id/${patientId}`,
        );

        if (data?.status !== "success") {
          this.patient = {};
          this.imageProfile = null;
          return;
        }

        this.patient = data.data || {};

        // ✅ imagem: usa URL pronta se existir, senão usa o util
        if (this.patient.profile_image_url) {
          this.imageProfile = this.patient.profile_image_url;
          return;
        }

        const imageUrl = await getProfileImage(this.patient.profile_image);

        if (typeof imageUrl === "string" && imageUrl.startsWith("blob:")) {
          this._patientBlobUrl = imageUrl;
        }

        this.imageProfile = imageUrl;
      } catch (error) {
        console.error("Erro ao buscar paciente:", error);
        this.patient = {};
        this.imageProfile = null;
      } finally {
        this.loadingPatient = false;
      }
    },

    revokePatientBlobUrl() {
      if (this._patientBlobUrl) {
        try {
          URL.revokeObjectURL(this._patientBlobUrl);
        } catch {}
        this._patientBlobUrl = null;
      }
    },

    // ================= AGENDAMENTO =================
    /**
     * Abre a MESMA janela de agendamento da agenda, com o paciente já
     * escolhido (salvia-XXX).
     *
     * Antes passava por uma grade semanal intermediária: o médico clicava em
     * "Agendar", escolhia um horário na grade, e só então caía no formulário.
     * Dois passos para chegar na tela que já sabe perguntar data e hora.
     *
     * Sem data e hora pré-selecionadas — quem as escolhe é o próprio diálogo,
     * que já valida disponibilidade e conflito.
     */
    handleScheduleAppointment() {
      this.AddNewEventPreSelectedPatient = {
        id: this.patient.id,
        name: this.patient.name,
        email: this.patient.email,
        phone: this.getPatientPhone(),
      };
      this.selectedTimeForNewEvent = null;
      this.openDialogAddNewEvent = true;
    },


    getPatientPhone() {
      const phones = this.patient?.phones || [];
      if (!phones.length) return null;

      return phones.find((p) => p.is_primary === 1)?.number ?? phones[0].number;
    },

    /**
     * salvia-837: quem chega por "Iniciar atendimento" (detalhe do compromisso,
     * na agenda) cai aqui com `?atendimento=novo` e ja encontra o atendimento
     * aberto, sem passar por uma tela de escolher paciente.
     *
     * O parametro e limpo logo depois, com `replace`: se ficasse na URL, um F5
     * ou o botao "voltar" reabririam o atendimento sozinhos, por cima do que o
     * medico estivesse fazendo. Ele e uma ordem de chegada, nao um estado da
     * pagina.
     *
     * `replace` e nao `push` pelo mesmo motivo: nao criar um passo a mais no
     * historico so para apagar um parametro.
     */
    openNewAppointmentFromRoute() {
      if (this.$route.query.atendimento !== "novo") return;

      this.openAppointmentDialog();
      // Depois do open, que zera o vínculo: o atendimento nasce já sabendo de
      // qual agendamento veio (histórico do kanban).
      this.sourceEventId = Number(this.$route.query.event) || null;

      const query = { ...this.$route.query };
      delete query.atendimento;
      delete query.event;
      // Rota identica quando nao ha outros parametros: o vue-router rejeita a
      // navegacao redundante, e isso aqui nao e erro.
      this.$router.replace({ query }).catch(() => {});
    },

    /**
     * salvia-850: quem clica na notificação "o paciente respondeu no WhatsApp"
     * chega com `?openChat=1` e já encontra a aba Chat aberta.
     *
     * O parâmetro era emitido pelo registry de notificações desde a salvia-720,
     * mas NENHUMA tela o lia — o clique levava ao prontuário e o chat não abria,
     * o mesmo sintoma que esta task veio corrigir nos recados.
     *
     * Mesma mecânica do `openNewAppointmentFromRoute` acima, e pelo mesmo motivo:
     * o parâmetro é uma ordem de chegada, não um estado da página. Se ficasse
     * na URL, um F5 ou o "voltar" reabririam o chat por cima do que o médico
     * estivesse fazendo.
     */
    openChatFromRoute() {
      if (this.$route.query.openChat !== "1") return;

      this.handleSectionClick("Chat");

      const query = { ...this.$route.query };
      delete query.openChat;
      this.$router.replace({ query }).catch(() => {});
    },

    /**
     * Histórico do kanban: "Abrir consulta" num card de dia passado chega com
     * `?appointment=ID` e já encontra aquela consulta aberta.
     *
     * Mesma mecânica do `openNewAppointmentFromRoute`: o parâmetro é uma ordem de
     * chegada, não um estado da página, e sai da URL com `replace`.
     */
    openAppointmentFromRoute() {
      const appointmentId = Number(this.$route.query.appointment) || null;
      if (!appointmentId) return;

      if (!this.appointmentDialog) this.openAppointmentDialog(appointmentId);

      const query = { ...this.$route.query };
      delete query.appointment;
      this.$router.replace({ query }).catch(() => {});
    },

    openAppointmentDialog(appointmentId = null) {
      this.selectedAppointmentId = appointmentId;
      this.sourceEventId = null;
      this.expandedPanels = null;
      this.appointmentDialog = true;
    },

    handleOpenAppointmentDialog(appointmentId = null) {
      this.openAppointmentDialog(appointmentId);
      this.showComponent = false;
    },

    closeAppointmentDialog() {
      this.appointmentDialog = false;
      this.selectedAppointmentId = null;
    },

    async handleAppointmentSaved() {
      await this.handleDataUpdated();

      if (this.currentComponent === "Appointments" && this.showComponent) {
        this.$nextTick(() => this.$root.$emit("reload-appointments"));
      }
    },

    async handleDataUpdated() {
      await this.getPatientById();
    },

    // ================= UI / SEÇÕES =================
    toggleDrawer() {
      this.showComponent = !this.showComponent;
      document.body.style.overflow = this.showComponent ? "hidden" : "";
    },

    handleSectionClick(sectionTitle) {
      const componentMap = {
        Anamneses: "AnamnesisContainer",
        Metas: "Goals",
        Exames: "Exams",
        Atendimentos: "Appointments",
        Prescrições: "Prescriptions",
        "Composição Corporal": "BodyComposition",
        "Itens Correlacionados": "CorrelatedItems",
        Chat: "ChatDoctor",
      };

      this.currentComponent = componentMap[sectionTitle] || null;
      // Força remontar o componente da aba a cada abertura, garantindo dados
      // frescos mesmo ao reabrir a mesma aba (o drawer fica montado, só oculto).
      this.drawerRefreshSeq += 1;
      this.showComponent = true;
    },

    // ================= CARREGAMENTO DOS CARDS =================

    /**
     * Inicia o estado de carregamento. Mantém o overlay borrado + spinner
     * até que TODOS os cards tenham emitido "card-loaded". Um timeout de
     * segurança garante que o overlay nunca fique preso caso algum card falhe.
     */
    startCardsLoading() {
      this.cardsLoading = true;
      this.loadedCardIds = [];
      if (this._loadingSafetyTimeout) clearTimeout(this._loadingSafetyTimeout);
      this._loadingSafetyTimeout = setTimeout(() => {
        this.cardsLoading = false;
      }, 8000);
    },

    /**
     * Cada card chama isso quando termina de carregar seus dados. Quando a
     * contagem alcança o total de cards, revela tudo de uma vez.
     */
    /** SALVIA-938: o card avisou quantos itens tem (ver mixins/cardItemCount). */
    setCardCount(cardId, count) {
      this.$set(this.cardCounts, cardId, count);
    },

    onCardLoaded(cardId) {
      if (!this.loadedCardIds.includes(cardId)) {
        this.loadedCardIds.push(cardId);
      }
      if (
        this.cards.length > 0 &&
        this.loadedCardIds.length >= this.cards.length
      ) {
        this.cardsLoading = false;
        if (this._loadingSafetyTimeout) {
          clearTimeout(this._loadingSafetyTimeout);
          this._loadingSafetyTimeout = null;
        }
      }
    },

    // ================= CARDS =================

    getCardCols() {
      return 12;
    },
    getCardSm() {
      return 12;
    },
    // Largura do card na grade do desktop (base = 4, ou seja 3 por linha):
    //   fullWidth (ex.: Histórico de Vida) -> 12, a linha inteira;
    //   compact   (álcool/hidratação)      -> 2, metade de um card normal. São
    //     cards de UM dado só, que a edição sobrescreve — ocupavam espaço de card
    //     cheio à toa (ajuste 18/08/2026). Dois compactos = um card normal.
    // No dialog da consulta tudo vira 12.
    cardSpan(card) {
      if (this.appointmentDialog) return 12;
      if (card && card.fullWidth) return 12;
      if (card && card.compact) return 2;
      return 4;
    },
    getCardMd(card) {
      return this.cardSpan(card);
    },
    getCardLg(card) {
      return this.cardSpan(card);
    },
    getCardXl(card) {
      return this.cardSpan(card);
    },

    /**
     * Um card gravou pelo atalho "+" enquanto a consulta estava aberta.
     * Repassa ao AppointmentDialog, que registra a nota (salvia-781).
     *
     * A ligação é direta (pai -> filho) porque esta página renderiza os dois;
     * um barramento global aqui só esconderia de quem lê o fluxo real.
     */
    forwardQuickInsert(payload) {
      const dialog = this.$refs.appointmentDialogRef;
      if (dialog && typeof dialog.registerQuickInsert === "function") {
        dialog.registerQuickInsert(payload);
      }
    },

    createCard() {
      // Cada card é um componente independente (recebe patient-id e o título, e
      // busca seus próprios dados). O título vem daqui, e não de um default no
      // card: repetido nos dois lugares, um deles ficava para trás numa
      // renomeação. A lista vem do manifesto compartilhado (dashboardCards) —
      // a mesma fonte que o AppointmentDialog usa para os botões de inserção.
      this.cards = dashboardCards.map((c) => ({
        id: c.id,
        title: c.title,
        component: c.card,
        fullWidth: !!c.fullWidth,
      }));
    },

    // ================= ORDEM CUSTOM DOS CARDS =================

    /**
     * Lê a ordem salva pelo usuário em user_preferences e reordena `this.cards`
     * conforme. Cards novos (não presentes na preferência) vão para o final, na
     * ordem original do config — assim adicionar um card novo no código nunca
     * "some" da interface dos usuários que já têm uma ordem salva.
     */
    async loadCustomCardOrder() {
      const savedIds = await getPreference(PREF_KEYS.DASHBOARD_CARDS_ORDER);
      if (!Array.isArray(savedIds) || savedIds.length === 0) return;

      const byId = new Map(this.cards.map((c) => [c.id, c]));
      const ordered = [];

      // Primeiro: cards na ordem salva (que ainda existem no config)
      for (const id of savedIds) {
        if (byId.has(id)) {
          ordered.push(byId.get(id));
          byId.delete(id);
        }
      }
      // Depois: cards novos que ainda não foram ordenados pelo usuário
      for (const card of byId.values()) ordered.push(card);

      this.cards = ordered;
    },

    toggleEditMode() {
      this.editMode = !this.editMode;
    },

    /**
     * Chamado pelo vuedraggable ao soltar um card. `this.cards` já está com a
     * nova ordem (porque usamos v-model). Salva no backend com debounce de 800ms.
     */
    onCardDragEnd() {
      if (this._saveOrderTimeout) clearTimeout(this._saveOrderTimeout);
      this._saveOrderTimeout = setTimeout(() => this.persistCardOrder(), 800);
    },

    async persistCardOrder() {
      this.savingOrder = true;
      try {
        const ids = this.cards.map((c) => c.id);
        await savePreference(PREF_KEYS.DASHBOARD_CARDS_ORDER, ids);
      } finally {
        this.savingOrder = false;
      }
    },

    async restoreDefaultOrder() {
      // Reseta para a ordem do config e persiste como vazio para o backend.
      this.createCard();
      await savePreference(PREF_KEYS.DASHBOARD_CARDS_ORDER, []);
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   Drawer lateral — design polido
   ═══════════════════════════════════════ */
/* ═══════════════════════════════════════
   Modo edição (reordenar cards)
   ═══════════════════════════════════════ */
.edit-mode-banner {
  border-radius: 10px !important;
  font-size: 13px;
}

/*
  salvia-851: os cards nascem com `my-5` (20px em cima e 20px embaixo), o que
  deixava 40px entre uma linha e a outra. Ajustado aqui, num lugar só, em vez de
  trocar a classe nos 19 cards — e a especificidade (duas classes) vence a do
  utilitário do Vuetify, que é de uma.
*/
.card-drag-area :deep(.v-card) {
  margin-top: 2px !important;
  margin-bottom: 2px !important;
}

.cards-draggable.is-edit-mode .card-drag-area {
  cursor: grab;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.cards-draggable.is-edit-mode .card-drag-area:active {
  cursor: grabbing;
}

.cards-draggable.is-edit-mode .card-drag-area :deep(.v-card) {
  outline: 2px dashed rgba(31, 77, 63, 0.45);
  outline-offset: -2px;
  position: relative;
}

.cards-draggable.is-edit-mode .card-drag-area :deep(.v-card)::after {
  content: "⋮⋮";
  position: absolute;
  top: 6px;
  right: 8px;
  font-size: 16px;
  font-weight: 800;
  color: rgba(31, 77, 63, 0.55);
  letter-spacing: -2px;
  pointer-events: none;
  z-index: 2;
}

/* Estados durante o drag (vuedraggable / sortablejs) */
.card-ghost {
  opacity: 0.35;
}

.card-chosen :deep(.v-card) {
  box-shadow: 0 12px 32px rgba(31, 77, 63, 0.30) !important;
}

.card-dragging :deep(.v-card) {
  transform: rotate(1.5deg);
  cursor: grabbing;
}

/* ═══════════════════════════════════════
   Drawer lateral — design polido
   ═══════════════════════════════════════ */
.drawer-section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px 6px 16px;
}

.drawer-section-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.patient-card {
  padding: 4px 4px 8px 4px;
}

.patient-card-mini {
  padding: 4px 0;
}

.patient-avatar {
  border: 2px solid var(--ds-blue-100);
  background-color: var(--ds-blue-bg);
}

.patient-name {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: var(--ds-text-strong) !important;
}

.patient-age {
  font-size: 11px !important;
  color: var(--ds-text-soft) !important;
  display: flex !important;
  align-items: center;
}

.info-item {
  min-height: 44px !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

/*
  salvia-773: o próximo agendamento é a informação que o médico procura nessa
  coluna — quando o paciente volta. Ganha fundo, borda de acento e um valor
  maior que os vizinhos para ser achado sem leitura.
*/
.next-appt {
  margin: 4px 8px;
  padding-left: 12px !important;
  padding-right: 12px !important;
  border-radius: 10px;
  border-left: 3px solid var(--ds-primary, #3e8e73);
  /* Verde do acento a 8%: funciona sobre fundo claro e escuro, sem precisar
     de um token por tema. */
  background: rgba(62, 142, 115, 0.08);
}

.next-appt__value {
  font-size: 15px !important;
  font-weight: 700 !important;
  color: var(--ds-primary, #3e8e73) !important;
}

/* Sem agendamento não é destaque: o texto recua para não competir. */
.next-appt__value--vazio {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: var(--ds-text-muted) !important;
  font-style: italic;
}

.next-appt__label {
  color: var(--ds-primary, #3e8e73) !important;
  opacity: 0.85;
}

.info-icon {
  min-width: 36px !important;
  margin-top: 8px !important;
  margin-bottom: 8px !important;
  margin-right: 8px !important;
  align-self: center !important;
}

.info-value {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: var(--ds-text-strong) !important;
}

.info-label {
  font-size: 10px !important;
  font-weight: 500 !important;
  color: var(--ds-text-muted) !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.action-buttons .v-btn {
  font-size: 10px !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  height: 32px !important;
}

.section-list .section-item {
  min-height: 44px !important;
  padding-left: 16px !important;
  padding-right: 12px !important;
  border-radius: 0;
  transition: background-color 0.15s ease, padding-left 0.15s ease;
}

.section-list .section-item:hover {
  background-color: var(--ds-blue-bg) !important;
  padding-left: 20px !important;
}

.section-title {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: var(--ds-text-700) !important;
}

/* ═══════════════════════════════════════
   Expansion panels quando na lateral (dialog aberto)
   ═══════════════════════════════════════ */
.collapsed-cards .v-expansion-panel {
  margin-bottom: 3px !important;
  border-radius: 8px !important;
  overflow: hidden;
}

.collapsed-cards .v-expansion-panel::before {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.collapsed-card-header {
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  color: var(--ds-text-600) !important;
  min-height: 32px !important;
  padding: 4px 8px !important;
}
/* SALVIA-938: título encolhe com reticências; o contador nunca some. */
.collapsed-card-title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.collapsed-card-count {
  /* O Vuetify dá flex: 1 a todo filho do header; sem isso o selo estica. */
  flex: 0 0 auto !important;
  margin: 0 6px 0 8px;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--ds-primary-surface);
  border: 1px solid var(--ds-primary-border);
  color: var(--ds-primary);
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
.collapsed-card-count--empty {
  background: transparent;
  border-color: var(--ds-border-soft);
  color: var(--ds-text-muted);
}

.collapsed-cards .v-expansion-panel-header__icon .v-icon {
  font-size: 18px !important;
}

/* :deep() é obrigatório: o __wrap é DOM interno do Vuetify e não recebe o
   atributo de scope; sem :deep() a regra não casava e os 24px laterais
   padrao do Vuetify permaneciam. Zeramos p/ o card usar toda a largura. */
.collapsed-cards :deep(.v-expansion-panel-content__wrap) {
  padding: 0 !important;
}

/* Remove margens dos cards dentro dos panels */
.collapsed-cards .v-card {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  box-shadow: none !important;
}

/* Remove a altura fixa dos cards quando dentro do panel */
.collapsed-cards .card-fixed-height {
  height: auto !important;
  min-height: unset !important;
}
/* ═══════════════════════════════════════
   Z-index do drawer quando dialog aberto
   ═══════════════════════════════════════ */
.drawer-behind-backdrop {
  z-index: 200 !important;
}

/* ═══════════════════════════════════════
   Backdrop escurecido
   ═══════════════════════════════════════ */
.dialog-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 198;
  backdrop-filter: blur(2px);
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* ═══════════════════════════════════════
   Cards container — estado normal
   ═══════════════════════════════════════ */
.cards-container {
  transition: all 0.3s ease-in-out;
}

/* ═══════════════════════════════════════
   Overlay de carregamento dos cards
   ═══════════════════════════════════════ */
.cards-loading-wrapper {
  position: relative;
}

/* Cards borrados/atenuados enquanto carregam */
.cards-blurred {
  filter: blur(4px);
  opacity: 0.55;
  pointer-events: none;
  user-select: none;
  transition: filter 0.35s ease, opacity 0.35s ease;
}

.cards-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 120px;
  gap: 16px;
  z-index: 5;
  pointer-events: none;
}

.cards-loading-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-primary);
  letter-spacing: 0.3px;
}

/* ═══════════════════════════════════════
   Cards container — dialog aberto (desktop ≥1600px)
   Cards ficam menores à direita com gap
   ═══════════════════════════════════════ */
.cards-container.dialog-open {
  position: fixed;
  right: 16px;
  top: 16px;
  /* Largura FIXA (alinhada com o `right` do form) — mais legível e sem vão. */
  width: 340px;
  height: calc(100vh - 32px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 3px; /* padding bem reduzido: mais espaço horizontal p/ os cards */
  margin: 0;
  z-index: 200;
  animation: slideInRight 0.3s ease-out;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* NÃO forçar os v-row/v-col INTERNOS dos cards a empilhar — os cards do
   atendimento já vêm em expansion-panels (coluna). Aquela regra antiga
   distorcia layouts internos (ex.: métricas lado a lado da Composição Corporal). */

/* Cards menores quando na lateral (sem transform: translateZ — criava stacking
   context e escondia o popover de hover atrás do layout do atendimento). */
.cards-container.dialog-open .v-card {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
  border-radius: 10px;
  font-size: 0.85rem;
}

/* Reduz padding interno dos cards na lateral (mais espaço p/ o conteúdo). */
.cards-container.dialog-open .v-card .v-card__text {
  padding: 6px 8px !important;
}

.cards-container.dialog-open .v-card .v-card__title {
  padding: 6px 8px !important;
  font-size: 0.8rem !important;
}

/* Legibilidade em coluna estreita: quebra texto em vez de estourar/distorcer. */
.cards-container.dialog-open .v-card,
.cards-container.dialog-open .v-card * {
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Scrollbar customizada */
.cards-container.dialog-open::-webkit-scrollbar {
  width: 5px;
}

.cards-container.dialog-open::-webkit-scrollbar-track {
  background: transparent;
}

.cards-container.dialog-open::-webkit-scrollbar-thumb {
  background: rgba(136, 136, 136, 0.4);
  border-radius: 4px;
}

.cards-container.dialog-open::-webkit-scrollbar-thumb:hover {
  background: rgba(85, 85, 85, 0.7);
}

/* ═══════════════════════════════════════
   Responsividade — larguras FIXAS por faixa, alinhadas com o `right` do form.
   Coluna de cards continua à direita (só encolhe), nunca vira flutuante nem
   some por baixo do form. Boa em MacBook (~1280–1512) e telas menores.
   ═══════════════════════════════════════ */
@media (max-width: 1440px) {
  .cards-container.dialog-open {
    width: 320px;
    right: 14px;
    top: 14px;
    height: calc(100vh - 28px);
  }
}

@media (max-width: 1200px) {
  .cards-container.dialog-open {
    width: 280px;
    right: 12px;
    top: 12px;
    height: calc(100vh - 24px);
  }
}

@media (max-width: 900px) {
  .cards-container.dialog-open {
    width: 240px;
    right: 10px;
    top: 10px;
    height: calc(100vh - 20px);
  }
}

/* ═══════════════════════════════════════
   Responsividade — mobile (≤768px): cards escondidos, form ocupa tudo
   ═══════════════════════════════════════ */
@media (max-width: 768px) {
  .cards-container.dialog-open {
    display: none;
  }
}

/* ═══════════════════════════════════════
   Campo de pesquisa
   ═══════════════════════════════════════ */
.v-text-field {
  width: 100%;
  max-width: 500px;
  margin-bottom: 20px;
}

/* ═══════════════════════════════════════
   Custom drawer (seções: Anamneses, etc.)
   ═══════════════════════════════════════ */
.custom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
}

.custom-drawer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: auto;
  max-height: 90vh;
  min-height: 70vh;
  background-color: var(--ds-surface);
  z-index: 999;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

.drawer-open {
  transform: translateY(0);
}

.drawer-content {
  height: 100%;
  overflow-y: auto;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
}
</style>
